import { BridgeComponent, BridgeElement } from "@hotwired/hotwire-native-bridge";

/**
 * DoubleActionComponent - Adds a sticky bottom bar with two or three action buttons
 *
 * ## Usage
 * Add the `bridge--double-action` controller to any element:
 *
 * ```erb
 * <div data-controller="bridge--double-action"
 *      data-bridge--double-action-primary-title-value="Download"
 *      data-bridge--double-action-primary-icon-value="arrow.down.circle"
 *      data-bridge--double-action-primary-color-value="#4ade80"
 *      data-bridge--double-action-primary-display-value="label"
 *      data-bridge--double-action-secondary-title-value="Map"
 *      data-bridge--double-action-secondary-icon-value="map"
 *      data-bridge--double-action-secondary-color-value="#e5e5e5"
 *      data-bridge--double-action-secondary-display-value="icon"
 *      data-bridge--double-action-secondary-accessibility-label-value="Open map"
 *      data-bridge--double-action-tertiary-icon-value="bookmark"
 *      data-bridge--double-action-tertiary-color-value="#e5e5e5"
 *      data-bridge--double-action-tertiary-display-value="icon"
 *      data-bridge--double-action-tertiary-accessibility-label-value="Save"
 *      class="hidden">
 *   <a href="/download" data-bridge--double-action-target="primary"></a>
 *   <a href="/map" data-bridge--double-action-target="secondary"></a>
 *   <a href="/save" data-bridge--double-action-target="tertiary"></a>
 * </div>
 * ```
 *
 * ## Stimulus Values
 * - `layout` (String, optional): `fit` (default) or `scroll` for horizontally scrolling labeled pills
 * - `primaryTitle` (String): Primary button text label
 * - `primaryIcon` (String): SF Symbol name for primary button
 * - `primaryColor` (String): Hex color for primary button background
 * - `primaryTextColor` (String, optional): Hex color for primary button text/icon
 * - `primaryDisplay` (String, optional): `label` (default) or `icon`
 * - `primaryAccessibilityLabel` (String, optional): VoiceOver label
 * - `primarySize` (Number, optional): Button height or icon-button diameter
 * - `primaryIconSize` (Number, optional): SF Symbol point size
 * - `secondaryTitle` (String): Secondary button text label
 * - `secondaryIcon` (String): SF Symbol name for secondary button
 * - `secondaryColor` (String): Hex color for secondary button background
 * - `secondaryTextColor` (String, optional): Hex color for secondary button text/icon
 * - `secondaryDisplay` (String, optional): `label` (default) or `icon`
 * - `secondaryAccessibilityLabel` (String, optional): VoiceOver label
 * - `secondarySize` (Number, optional): Button height or icon-button diameter
 * - `secondaryIconSize` (Number, optional): SF Symbol point size
 * - `tertiaryTitle` (String, optional): Tertiary button text label
 * - `tertiaryIcon` (String, optional): SF Symbol name for tertiary button
 * - `tertiaryColor` (String, optional): Hex color for tertiary button background
 * - `tertiaryTextColor` (String, optional): Hex color for tertiary button text/icon
 * - `tertiaryDisplay` (String, optional): `label` or `icon`
 * - `tertiaryAccessibilityLabel` (String, optional): VoiceOver label
 * - `tertiarySize` (Number, optional): Button height or icon-button diameter
 * - `tertiaryIconSize` (Number, optional): SF Symbol point size
 *
 * ## Targets
 * - `primary`: Element to click when primary button is tapped
 * - `secondary`: Element to click when secondary button is tapped
 * - `tertiary`: Element to click when tertiary button is tapped
 *
 * ## Behavior
 * - Displays two buttons, or three when a tertiary title or icon is provided
 * - Supports label and icon-only display styles on iOS
 * - Uses glassEffect on iOS 26+
 * - When tapped, triggers the respective target element's click handler
 */
export default class extends BridgeComponent {
  static component = "double-action";
  static targets = ["primary", "secondary", "tertiary"];
  static values = {
    layout: { type: String, default: "fit" },
    primaryTitle: String,
    primaryIcon: String,
    primaryColor: String,
    primaryTextColor: String,
    primaryDisplay: String,
    primaryAccessibilityLabel: String,
    primarySize: Number,
    primaryIconSize: Number,
    secondaryTitle: String,
    secondaryIcon: String,
    secondaryColor: String,
    secondaryTextColor: String,
    secondaryDisplay: String,
    secondaryAccessibilityLabel: String,
    secondarySize: Number,
    secondaryIconSize: Number,
    tertiaryTitle: String,
    tertiaryIcon: String,
    tertiaryColor: String,
    tertiaryTextColor: String,
    tertiaryDisplay: String,
    tertiaryAccessibilityLabel: String,
    tertiarySize: Number,
    tertiaryIconSize: Number,
  };

  connect() {
    super.connect();
    this.#notifyBridgeOfConnect();
  }

  disconnect() {
    super.disconnect();
    this.send("disconnect", {}, () => { });
  }

  #notifyBridgeOfConnect() {
    const data = {
      layout: this.layoutValue,
      primaryTitle: this.hasPrimaryTitleValue ? this.primaryTitleValue : "Primary",
      primaryIcon: this.hasPrimaryIconValue ? this.primaryIconValue : "star.fill",
      primaryColor: this.hasPrimaryColorValue ? this.primaryColorValue : "#4ade80",
      primaryTextColor: this.hasPrimaryTextColorValue ? this.primaryTextColorValue : null,
      primaryDisplay: this.hasPrimaryDisplayValue ? this.primaryDisplayValue : null,
      primaryAccessibilityLabel: this.hasPrimaryAccessibilityLabelValue ? this.primaryAccessibilityLabelValue : null,
      primarySize: this.hasPrimarySizeValue ? this.primarySizeValue : null,
      primaryIconSize: this.hasPrimaryIconSizeValue ? this.primaryIconSizeValue : null,
      secondaryTitle: this.hasSecondaryTitleValue ? this.secondaryTitleValue : "Secondary",
      secondaryIcon: this.hasSecondaryIconValue ? this.secondaryIconValue : "square.grid.2x2",
      secondaryColor: this.hasSecondaryColorValue ? this.secondaryColorValue : "#e5e5e5",
      secondaryTextColor: this.hasSecondaryTextColorValue ? this.secondaryTextColorValue : null,
      secondaryDisplay: this.hasSecondaryDisplayValue ? this.secondaryDisplayValue : null,
      secondaryAccessibilityLabel: this.hasSecondaryAccessibilityLabelValue ? this.secondaryAccessibilityLabelValue : null,
      secondarySize: this.hasSecondarySizeValue ? this.secondarySizeValue : null,
      secondaryIconSize: this.hasSecondaryIconSizeValue ? this.secondaryIconSizeValue : null,
      tertiaryTitle: this.hasTertiaryTitleValue ? this.tertiaryTitleValue : null,
      tertiaryIcon: this.hasTertiaryIconValue ? this.tertiaryIconValue : null,
      tertiaryColor: this.hasTertiaryColorValue ? this.tertiaryColorValue : null,
      tertiaryTextColor: this.hasTertiaryTextColorValue ? this.tertiaryTextColorValue : null,
      tertiaryDisplay: this.hasTertiaryDisplayValue ? this.tertiaryDisplayValue : null,
      tertiaryAccessibilityLabel: this.hasTertiaryAccessibilityLabelValue ? this.tertiaryAccessibilityLabelValue : null,
      tertiarySize: this.hasTertiarySizeValue ? this.tertiarySizeValue : null,
      tertiaryIconSize: this.hasTertiaryIconSizeValue ? this.tertiaryIconSizeValue : null,
    };

    this.send("connect", data, (message) => {
      // Callback when native button is tapped
      const button = message?.data?.button;

      if (button === "primary" && this.hasPrimaryTarget) {
        new BridgeElement(this.primaryTarget).click();
      } else if (button === "secondary" && this.hasSecondaryTarget) {
        new BridgeElement(this.secondaryTarget).click();
      } else if (button === "tertiary" && this.hasTertiaryTarget) {
        new BridgeElement(this.tertiaryTarget).click();
      }
    });
  }
}
